/**
 * Client for the backend-proxied Verifieddit Cloud Run validation API.
 * Sends file data to POST /api/drop/validate for server-side C2PA verification.
 * Returns structured verification result or null on failure.
 */

export interface ValidateApiResponse {
  status: 'verified' | 'authentic' | 'invalid' | 'unsigned'
  c2pa: {
    issuer: string | null
    signedAt: string | null
    claimGenerator: string | null
  } | null
  aiAnalysis: {
    aiDetected: boolean | null
    confidence: number | null
  } | null
  fallback?: 'wasm'
  error?: string
}

const MAX_API_FILE_SIZE = 10 * 1024 * 1024 // 10MB — Cloud Run limit

/**
 * Validate media via Verifieddit Cloud Run API (through backend proxy).
 * Returns null if file is too large (caller should use WASM fallback).
 * Returns null on network/API error (caller should use WASM fallback).
 */
export async function validateMediaViaApi(
  file: File,
  fileHash: string,
): Promise<ValidateApiResponse | null> {
  if (file.size > MAX_API_FILE_SIZE) {
    console.log(`[validate] File ${file.name} exceeds 10MB (${file.size}), skipping API`)
    return null
  }

  try {
    const buffer = await file.arrayBuffer()
    const bytes = new Uint8Array(buffer)
    let binary = ''
    const chunkSize = 8192
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
    }
    const base64 = btoa(binary)

    const apiBase = typeof window !== 'undefined' && window.location.hostname === 'www.thephenom.app'
      ? 'https://drop.thephenom.app' : ''
    const res = await fetch(`${apiBase}/api/drop/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileHash,
        filename: file.name,
        contentType: file.type,
        fileData: base64,
      }),
    })

    if (res.status === 413) {
      return null
    }

    if (!res.ok) {
      console.warn(`[validate] API returned ${res.status}`)
      return null
    }

    return await res.json() as ValidateApiResponse
  } catch (err) {
    console.error('[validate] API call failed:', err)
    return null
  }
}
